import { useFormContext, useWatch } from "react-hook-form";

import { AutoFormFieldProps } from "@autoform/react";

import { EntityRefPicker } from "@/components/fields/entity-ref-picker";
import {
  MovieSummary,
  useMovieRepository,
} from "@/pages/movies/hooks/use-movie-repository";
import { ScreeningMovieRef } from "@/pages/screenings/schemas/screening.edit.form";

/**
 * The screening's movie. The form holds the denormalized `movie` copy for display; on save only
 * its id goes out, as `movieId`, and the backend re-copies the movie (title, poster, duration,
 * genres) and re-derives `endDate`.
 *
 * This is also where a screening's images live: a screening has no media of its own, it shows
 * its movie's poster — so the poster is edited on the movie, which the link opens.
 */
export function ScreeningMovieField({ id }: AutoFormFieldProps) {
  const { search } = useMovieRepository();
  const form = useFormContext();
  const movie: ScreeningMovieRef | null | undefined = useWatch({ name: id });

  const pick = (summary: MovieSummary) => {
    if (!summary.id || summary.id === movie?.id) return;
    form.setValue(
      id,
      {
        id: summary.id,
        title: summary.name,
        media: summary.posterUrl
          ? { photos: [{ url: summary.posterUrl, blurhash: "" }], teasers: [] }
          : null,
      },
      { shouldDirty: true },
    );
  };

  return (
    <EntityRefPicker<MovieSummary>
      current={
        movie?.id
          ? {
              name: movie.title ?? movie.id,
              subtitle: "Poster and images are edited on the movie",
              imageUrl: movie.media?.photos?.[0]?.url,
              href: `/movies/edit/${movie.id}`,
            }
          : null
      }
      emptyLabel="No movie"
      onSearch={search}
      accessorFn={(m) => ({
        id: m.id,
        name: [m.name, m.releaseYear && `(${m.releaseYear})`, m.director]
          .filter(Boolean)
          .join(" "),
        iconUrl: m.posterUrl,
      })}
      onPick={pick}
    />
  );
}
